import axios from 'axios';

// API Base URL - use deployed function in production, local server in development
const API_BASE = (() => {
  const prodUrl = 'https://us-central1-silas-5fdd2.cloudfunctions.net/api';
  const localUrl = 'http://localhost:5001';
  
  // Check if we're forcing production API
  if (process.env.REACT_APP_USE_PROD_API === 'true') {
    console.log('🌐 Using production API endpoint (forced):', prodUrl);
    return prodUrl;
  }
  
  // Check if we're in production build
  if (process.env.NODE_ENV === 'production') {
    console.log('🌐 Using production API endpoint:', prodUrl);
    return prodUrl;
  }
  
  // Default to local for development
  console.log('🌐 Using local API endpoint:', localUrl);
  return localUrl;
})();

// Log the Stripe configuration
console.log('💳 Stripe Configuration:', {
  apiEndpoint: API_BASE,
  environment: process.env.NODE_ENV,
  useProductionApi: process.env.REACT_APP_USE_PROD_API
});

// Types for API responses
export interface SendOtpResponse {
  success: boolean;
  message: string;
  phone?: string;
  error?: string;
}

export interface VerifyOtpResponse {
  success: boolean;
  message: string;
  accessToken?: string;
  refreshToken?: string;
  user?: {
    phone: string;
    verified: boolean;
  };
  error?: string;
}

export interface RefreshTokenResponse {
  success: boolean;
  message: string;
  accessToken?: string;
  error?: string;
}

export interface UserProfileResponse {
  success: boolean;
  user?: {
    phone: string;
    verified: boolean;
    lastLogin: number;
    createdAt?: any;
  };
  message?: string;
  error?: string;
}

export interface LogoutResponse {
  success: boolean;
  message: string;
  error?: string;
}

export interface CreateUserProfileResponse {
  success: boolean;
  id?: string;
  message: string;
  error?: string;
}

export interface AddPaidUserDataResponse {
  success: boolean;
  id?: string;
  message: string;
  error?: string;
}

export interface UserProfileDataResponse {
  success: boolean;
  profiles?: Array<{
    id: string;
    email: string;
    phoneNumber: string;
    fullName: string;
    isPaidUser: boolean;
    accountType: string;
    status: string;
    createdAt: any;
    updatedAt: any;
  }>;
  message?: string;
  error?: string;
}

export interface ApiError {
  message: string;
  status?: number;
}

// Legacy token management - keeping minimal methods for phone verification
// Clerk handles main authentication, but phone verification still uses these temporarily
export const TokenManager = {
  getAccessToken: (): string | null => {
    return localStorage.getItem('accessToken');
  },
  
  getRefreshToken: (): string | null => {
    return localStorage.getItem('refreshToken');
  },
  
  setTokens: (accessToken: string, refreshToken: string): void => {
    // Only store tokens temporarily for phone verification
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  },
  
  clearTokens: (): void => {
    // Clean up any legacy tokens
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userPhone');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userFullName');
    localStorage.removeItem('phoneVerified');
    localStorage.removeItem('isInSignupFlow');
  }
};

// Create axios instance with interceptors
const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
});

// Note: Authentication now handled by Clerk
// No token interceptors needed since we use Clerk-only authentication

// Refresh access token function
const refreshAccessToken = async (refreshToken: string): Promise<string> => {
  try {
    const response = await axios.post(`${API_BASE}/auth/refreshToken`, {
      refreshToken: refreshToken
    });
    
    if (response.data.success && response.data.accessToken) {
      return response.data.accessToken;
    } else {
      throw new Error('Failed to refresh token');
    }
  } catch (error: any) {
    console.error('Error refreshing token:', error);
    throw new Error('Failed to refresh access token');
  }
};

// Send OTP API function
export const sendOtp = async (phone: string): Promise<SendOtpResponse> => {
  try {
    console.log('📱 Sending OTP to phone:', phone);
    console.log('🌐 API URL:', `${API_BASE}/auth/sendOtp`);
    
    // Ensure phone number is in E.164 format
    let formattedPhone = phone;
    if (!formattedPhone.startsWith('+')) {
      formattedPhone = `+${formattedPhone}`;
    }
    
    // Remove any non-digit characters except the leading +
    formattedPhone = formattedPhone.replace(/[^+\d]/g, '');
    
    console.log('📱 Formatted phone (E.164):', formattedPhone);
    
    const response = await axios.post(`${API_BASE}/auth/sendOtp`, {
      phone: phone
    });
    
    return response.data;
  } catch (error: any) {
    console.error('Error sending OTP:', error);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to send OTP. Please try again.');
    }
  }
};

// Verify OTP API function
export const verifyOtp = async (phone: string, otp: string): Promise<VerifyOtpResponse> => {
  try {
    const response = await axios.post(`${API_BASE}/auth/verifyOtp`, {
      phone: phone,
      otp: otp
    });
    
    return response.data;
  } catch (error: any) {
    console.error('Error verifying OTP:', error);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to verify OTP. Please try again.');
    }
  }
};

// Refresh token API function
export const refreshToken = async (refreshToken: string): Promise<RefreshTokenResponse> => {
  try {
    const response = await axios.post(`${API_BASE}/auth/refreshToken`, {
      refreshToken: refreshToken
    });
    
    return response.data;
  } catch (error: any) {
    console.error('Error refreshing token:', error);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to refresh token. Please try again.');
    }
  }
};

// Get user profile API function - DEPRECATED (use Clerk data instead)
export const getUserProfile = async (): Promise<UserProfileResponse> => {
  // Since we switched to Clerk-only authentication, this function is no longer used
  // All user data should come from Clerk's useUser() hook
  throw new Error('Access token is required - This API is deprecated. Use Clerk user data instead.');
};

// Logout API function
export const logout = async (): Promise<LogoutResponse> => {
  try {
    const refreshToken = TokenManager.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token found');
    }
    
    const response = await axios.post(`${API_BASE}/auth/logout`, {
      refreshToken: refreshToken
    });
    
    // Clear tokens from localStorage regardless of API response
    TokenManager.clearTokens();
    
    return response.data;
  } catch (error: any) {
    console.error('Error during logout:', error);
    
    // Clear tokens even if logout API fails
    TokenManager.clearTokens();
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to logout. Please try again.');
    }
  }
};

// Helper function to format phone number (ensure it starts with +)
export const formatPhoneNumber = (phone: string): string => {
  return phone.startsWith('+') ? phone : `+${phone}`;
};

// Create Customer for Free Trial (No Payment)
export const createCustomerForTrial = async (email: string, name: string, planName?: string) => {
  try {
    console.log('🆓 Creating customer for free trial with params:', { email, name, planName });
    console.log('🌐 API URL:', `${API_BASE}/payment/create-customer-for-trial`);

    const response = await axios.post(`${API_BASE}/payment/create-customer-for-trial`, {
      email,
      name,
      planName
    });
    
    console.log('✅ Trial customer created successfully:', response.data);

    if (!response.data.clientSecret) {
      console.error('❌ No client secret in response:', response.data);
      throw new Error('Invalid setup intent response from server');
    }

    if (!response.data.customerId) {
      console.error('❌ No customer ID in response:', response.data);
      throw new Error('Invalid customer creation response from server');
    }

    return response.data;
  } catch (error: any) {
    console.error('❌ Error creating trial customer:', {
      error: error,
      response: error.response?.data,
      status: error.response?.status
    });
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status === 404) {
      throw new Error('Trial service not found. Please check the API endpoint.');
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error. Please check your connection.');
    } else {
      throw new Error(error.message || 'Unknown error occurred');
    }
  }
};

// Create Customer & PaymentIntent API function (Combined)
export const createCustomerAndPaymentIntent = async (amount: number, currency: string = 'usd', planName?: string, email?: string, name?: string) => {
  try {
    console.log('🔄 Creating customer and payment intent with params:', { amount, currency, planName, email, name });
    console.log('🌐 API URL:', `${API_BASE}/payment/create-customer-and-payment-intent`);

    const response = await axios.post(`${API_BASE}/payment/create-customer-and-payment-intent`, {
      amount,
      currency,
      planName,
      email,
      name
    });
    
    console.log('✅ Customer and payment intent created successfully:', response.data);

    if (!response.data.clientSecret) {
      console.error('❌ No client secret in response:', response.data);
      throw new Error('Invalid payment intent response from server');
    }

    if (!response.data.customerId) {
      console.error('❌ No customer ID in response:', response.data);
      throw new Error('Invalid customer creation response from server');
    }

    return response.data;
  } catch (error: any) {
    console.error('❌ Error creating customer and payment intent:', {
      error: error,
      response: error.response?.data,
      status: error.response?.status
    });
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status === 404) {
      throw new Error('Payment service not found. Please check the API endpoint.');
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to create customer and payment intent. Please try again.');
    }
  }
};

// Legacy Create Stripe Customer API function (for backward compatibility)
export const createStripeCustomer = async (email: string, name?: string) => {
  try {
    console.log('🔄 Creating Stripe customer for:', email);
    console.log('🌐 API URL:', `${API_BASE}/payment/create-customer`);

    const response = await axios.post(`${API_BASE}/payment/create-customer`, {
      email,
      name
    });
    
    console.log('✅ Stripe customer created successfully:', response.data);

    if (!response.data.customerId) {
      console.error('❌ No customer ID in response:', response.data);
      throw new Error('Invalid customer creation response from server');
    }

    return response.data;
  } catch (error: any) {
    console.error('❌ Error creating Stripe customer:', {
      error: error,
      response: error.response?.data,
      status: error.response?.status
    });
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status === 404) {
      throw new Error('Customer creation service not found. Please check the API endpoint.');
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to create customer. Please try again.');
    }
  }
};

// Create payment intent API function (Updated to include customerId)
export const createPaymentIntent = async (amount: number, currency: string = 'usd', planName?: string, customerId?: string) => {
  try {
    console.log('🔄 Creating payment intent with params:', { amount, currency, planName, customerId });
    console.log('🌐 API URL:', `${API_BASE}/payment/create-payment-intent`);

    const response = await axios.post(`${API_BASE}/payment/create-payment-intent`, {
      amount,
      currency,
      planName,
      customerId
    });
    
    console.log('✅ Payment intent created successfully:', response.data);

    if (!response.data.clientSecret) {
      console.error('❌ No client secret in response:', response.data);
      throw new Error('Invalid payment intent response from server');
    }

    return response.data;
  } catch (error: any) {
    console.error('❌ Error creating payment intent:', {
      error: error,
      response: error.response?.data,
      status: error.response?.status
    });
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status === 404) {
      throw new Error('Payment service not found. Please check the API endpoint.');
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to create payment intent. Please try again.');
    }
  }
};

// Retrieve Payment Method API function
export const retrievePaymentMethod = async (paymentMethodId: string) => {
  try {
    console.log('🔄 Retrieving payment method:', paymentMethodId);
    console.log('🌐 API URL:', `${API_BASE}/payment/retrieve-payment-method`);

    const response = await axios.get(`${API_BASE}/payment/retrieve-payment-method`, {
      params: { paymentMethodId }
    });
    
    console.log('✅ Payment method retrieved successfully:', response.data);

    if (!response.data.paymentMethod) {
      console.error('❌ No payment method in response:', response.data);
      throw new Error('Invalid payment method response from server');
    }

    return response.data;
  } catch (error: any) {
    console.error('❌ Error retrieving payment method:', {
      error: error,
      response: error.response?.data,
      status: error.response?.status
    });
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status === 404) {
      throw new Error('Payment method not found. Please check the payment method ID.');
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to retrieve payment method. Please try again.');
    }
  }
};

// Create user profile API function
export const createUserProfile = async (email: string, phoneNumber: string, fullName: string, isPaidUser: boolean): Promise<CreateUserProfileResponse> => {
  try {
    const response = await apiClient.post('/user/user-profile', {
      email,
      phoneNumber,
      fullName,
      isPaidUser
    });
    
    return response.data;
  } catch (error: any) {
    console.error('Error creating user profile:', error);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to create user profile. Please try again.');
    }
  }
};

// Add paid user data API function
export const addPaidUserData = async (
  email: string, 
  phoneNumber: string, 
  fullName: string,
  subscriptionPlan?: string, 
  subscriptionPrice?: number, 
  stripePaymentIntentId?: string
): Promise<AddPaidUserDataResponse> => {
  try {
    const response = await apiClient.post('/user/paid-user-data', {
      email,
      phoneNumber,
      fullName,
      subscriptionPlan,
      subscriptionPrice,
      stripePaymentIntentId
    });
    
    return response.data;
  } catch (error: any) {
    console.error('Error adding paid user data:', error);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to add paid user data. Please try again.');
    }
  }
};

// Create trial subscription (7-day free trial)
export const createTrialSubscription = async (subscriptionData: {
  email: string;
  phoneNumber: string;
  fullName: string;
  planId: string;
  stripeCustomerId: string;
  stripePaymentMethodId: string;
  cardDetails?: {
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
    cardholderName: string;
  };
}) => {
  try {
    console.log('🆓 Creating trial subscription:', subscriptionData);
    
    const response = await axios.post(`${API_BASE}/user/trial-subscription`, subscriptionData);
    console.log('✅ Trial subscription created successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Error creating trial subscription:', error);
    console.error('Error response:', error.response?.data);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to create trial subscription. Please try again.');
    }
  }
};

export const getPaymentMethodDetails = async (paymentMethodId: string) => {
  try {
    console.log('🔄 Fetching payment method details for:', paymentMethodId);
    const response = await axios.get(`${API_BASE}/payment/retrieve-payment-method?paymentMethodId=${paymentMethodId}`);
    console.log('✅ Payment method details fetched:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Error fetching payment method details:', error);
    if (error.response) {
      console.error('Error response:', error.response.data);
    }
    throw new Error(error.response?.data?.message || 'Failed to fetch payment method details');
  }
};

// Cancel trial subscription (within 7 days)
export const cancelTrialSubscription = async (data: {
  email: string;
  subscriptionId: string;
  reason?: string;
}) => {
  try {
    console.log('🚫 Cancelling trial subscription:', data);
    
    const response = await axios.post(`${API_BASE}/user/cancel-trial-subscription`, data);
    console.log('✅ Trial subscription cancelled successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Error cancelling trial subscription:', error);
    console.error('Error response:', error.response?.data);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to cancel trial subscription. Please try again.');
    }
  }
};

// Get user profile data with fullName API function

// Process payment for plan upgrade
export const processPlanUpgradePayment = async (data: {
  email: string;
  planId: string;
  amount: number;
  stripeCustomerId: string;
  stripePaymentMethodId: string;
  subscriptionId: string;
}): Promise<{
  success: boolean;
  paymentIntentId?: string;
  amount?: number;
  status?: string;
  message?: string;
  error?: string;
}> => {
  try {
    console.log('💳 Processing plan upgrade payment:', data);
    
    const response = await axios.post(`${API_BASE}/payment/process-plan-upgrade`, data);
    console.log('✅ Plan upgrade payment processed:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Error processing plan upgrade payment:', error);
    console.error('Error response:', error.response?.data);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to process plan upgrade payment. Please try again.');
    }
  }
};

// ========================
// BILLING MANAGEMENT APIS
// ========================

/**
 * Create a new billing record
 */
export const createBillingRecord = async (billingData: {
  email: string;
  userId: string;
  stripeCustomerId: string;
  subscriptionId: string;
  amountCharged: number;
  stripePaymentIntentId?: string;
}): Promise<{ success: boolean; billingId?: string; message?: string; error?: string }> => {
  try {
    console.log('💳 Creating billing record:', billingData);
    
    const response = await axios.post(`${API_BASE}/billing/create`, billingData);
    console.log('✅ Billing record created:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Error creating billing record:', error);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to create billing record. Please try again.');
    }
  }
};

/**
 * Get billing history for a user
 */
export const getBillingHistory = async (email: string): Promise<{
  success: boolean;
  billingHistory?: Array<{
    id: string;
    email: string;
    userId: string;
    stripeCustomerId: string;
    subscriptionId: string;
    amountCharged: number;
    stripePaymentIntentId?: string;
    createdAt: string;
  }>;
  message?: string;
  error?: string;
}> => {
  try {
    console.log('📊 Fetching billing history for:', email);
    
    const response = await axios.get(`${API_BASE}/billing/history/${encodeURIComponent(email)}`);
    console.log('✅ Billing history retrieved:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Error fetching billing history:', error);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to fetch billing history. Please try again.');
    }
  }
};

/**
 * Get a specific billing record by ID
 */
export const getBillingRecord = async (billingId: string): Promise<{
  success: boolean;
  billingRecord?: {
    id: string;
    email: string;
    userId: string;
    stripeCustomerId: string;
    subscriptionId: string;
    amountCharged: number;
    stripePaymentIntentId?: string;
    createdAt: string;
  };
  message?: string;
  error?: string;
}> => {
  try {
    console.log('📊 Fetching billing record:', billingId);
    
    const response = await axios.get(`${API_BASE}/billing/${billingId}`);
    console.log('✅ Billing record retrieved:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Error fetching billing record:', error);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to fetch billing record. Please try again.');
    }
  }
};

export const getUserProfileData = async (email: string): Promise<UserProfileDataResponse> => {
  try {
    const response = await axios.get(`${API_BASE}/user/user-profile`, {
      params: { email }
    });
    return response.data;
  } catch (error: any) {
    console.error('Error getting user profile data:', error);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to get user profile data. Please try again.');
    }
  }
};


